import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ImageCacheProvider } from "../../../context/ImageCacheContext";
import { fetchSitelineEntries, sitelineLink } from "../api/sitelineFeed";
import type { SitelineEntry } from "../types";
import SitelineCard from "./SitelineCard";
import "../styles/siteline.css";

/**
 * Continuous siteline feed embedded on the home screen.
 *
 * Reuses the real `SitelineCard` renderers (coffee cards, AOTD tiles, card
 * pulls, announcements, comments, …) so the home gets the *actual* feed cards.
 * It deliberately drops the full-page chrome (sticky masthead, fixed "N new"
 * pill, scroll-to-top, its own background) and instead fills its column height
 * with an internally-scrolling list, so the page stays contained and the feed
 * feels continuous beside the rest of the home.
 *
 * Posts keep the standalone /siteline card look (boxed white cards + the solid
 * kind chip), just with a bigger actor emoji; on mobile the feed flows
 * continuously with the page instead of scrolling inside its own column. The
 * home-embed tweaks live in siteline.css scoped under `.siteline-home`; the
 * standalone `/siteline` page keeps its own background + masthead untouched
 * (those styles are scoped to `.siteline-root` without `.siteline-home`).
 */

/** Cap the embedded feed so we don't render the entire AOTD archive on the home. */
const HOME_FEED_CAP = 30;

export interface SitelineHomeProps {
  /**
   * Render the header + an "it's quiet" note instead of collapsing to nothing
   * when the feed is empty. The inline home embed hides itself (keeps the home
   * uncluttered); the desktop dock passes `true` because the user explicitly
   * opened the drawer and an empty box is better than a blank one.
   */
  showWhenEmpty?: boolean;
  /** extra class on the root — the dock uses it to drop the inline-only rules */
  className?: string;
}

const SitelineHome: React.FC<SitelineHomeProps> = ({ showWhenEmpty = false, className }) => {
  const [entries, setEntries] = useState<SitelineEntry[] | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    fetchSitelineEntries()
      .then((data) => { if (alive) setEntries(data); })
      .catch(() => { if (alive) setEntries([]); });
    return () => { alive = false; };
  }, []);

  const isEmpty = !entries || entries.length === 0;
  // Hide entirely until there's something to show — keeps the home uncluttered.
  if (isEmpty && !showWhenEmpty) return null;

  const visible = (entries ?? []).slice(0, HOME_FEED_CAP);
  const openItem = (e: SitelineEntry) => {
    const to = sitelineLink(e);
    if (to) navigate(to);
  };
  const goFeed = () => navigate("/siteline");

  return (
    <ImageCacheProvider>
      <div className={"siteline-root siteline-home" + (className ? ` ${className}` : "")}>
        <div className="siteline-home-head">
          <span className="mast-title">siteline <span className="live-dot" /></span>
          <button type="button" className="siteline-home-all" onClick={goFeed}>
            open feed →
          </button>
        </div>
        <div className="feed-col siteline-home-feed">
          {isEmpty ? (
            <p className="siteline-home-quiet">
              {entries === null ? "loading the siteline…" : "quiet in here. nothing has happened yet."}
            </p>
          ) : (
            visible.map((e) => (
              <SitelineCard key={e.id} e={e} onOpen={openItem} />
            ))
          )}
          {/* shown when stacked (mobile/tablet) — on desktop you scroll the column */}
          <button type="button" className="siteline-home-more" onClick={goFeed}>
            see the whole siteline →
          </button>
        </div>
      </div>
    </ImageCacheProvider>
  );
};

export default SitelineHome;
